/* ==========================================================================
   SYNOVA — Interactions
   JS natif, sans dépendance. Chargé en `defer`.

   1. Reveal au scroll
   2. Carrousels horizontaux
   3. Vidéos UGC (chargement différé + lecture à la visibilité)
   4. Sticky add to cart
   5. Modals (guide des tailles, pop-ups)
   6. Pop-up newsletter de bienvenue
   7. Feedback d'ajout au panier
   8. Barre d'annonces rotative
   ========================================================================== */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  /** Sur desktop Horizon, c'est .page-wrapper qui défile, pas la fenêtre. */
  function getScroller() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1) {
      return wrapper;
    }
    return window;
  }

  /* ======================================================================
     1. REVEAL AU SCROLL
     ====================================================================== */

  function initReveal() {
    const targets = document.querySelectorAll('[data-syn-reveal]:not(.syn-is-revealed)');
    if (!targets.length) return;

    // Mouvement réduit : on révèle tout immédiatement, sans transition.
    if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('syn-is-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('syn-is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
    );

    targets.forEach((el, index) => {
      // Décalage en cascade pour les éléments d'un même groupe
      const group = el.closest('[data-syn-reveal-group]');
      if (group && !el.style.getPropertyValue('--syn-reveal-delay')) {
        const siblings = Array.from(
          group.querySelectorAll('[data-syn-reveal]')
        );
        const position = siblings.indexOf(el);
        if (position > 0) {
          el.style.setProperty(
            '--syn-reveal-delay',
            Math.min(position * 70, 420) + 'ms'
          );
        }
      }
      observer.observe(el);
    });
  }

  /* ======================================================================
     2. CARROUSELS HORIZONTAUX
     ====================================================================== */

  function initScrollers() {
    document.querySelectorAll('[data-syn-scroller]').forEach((root) => {
      if (root.dataset.synScrollerReady === 'true') return;
      root.dataset.synScrollerReady = 'true';

      const track = root.querySelector('[data-syn-scroller-track]');
      const prev = root.querySelector('[data-syn-scroller-prev]');
      const next = root.querySelector('[data-syn-scroller-next]');
      if (!track) return;

      function step() {
        const first = track.firstElementChild;
        if (!first) return track.clientWidth * 0.8;
        const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
        return first.getBoundingClientRect().width + gap;
      }

      function scrollBy(direction) {
        track.scrollBy({
          left: step() * direction,
          behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
        });
      }

      prev && prev.addEventListener('click', () => scrollBy(-1));
      next && next.addEventListener('click', () => scrollBy(1));

      function updateButtons() {
        if (!prev || !next) return;
        const max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
      }

      track.addEventListener('scroll', updateButtons, { passive: true });
      window.addEventListener('resize', updateButtons, { passive: true });
      updateButtons();
    });
  }

  /* ======================================================================
     3. VIDÉOS UGC — chargement différé et lecture à la visibilité
     ====================================================================== */

  function initVideos() {
    const cards = document.querySelectorAll('[data-syn-video]:not([data-syn-video-ready])');
    if (!cards.length) return;

    /**
     * On ne charge la source qu'à l'approche de l'écran : aucune vidéo
     * lourde n'est téléchargée à l'arrivée sur la page.
     */
    const loadObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const card = entry.target;
          const video = card.querySelector('video');
          if (video && video.dataset.src && !video.src) {
            video.src = video.dataset.src;
            video.load();
          }
          loadObserver.unobserve(card);
        });
      },
      { rootMargin: '400px 0px' }
    );

    /** Lecture uniquement quand la vidéo est réellement visible. */
    const playObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const card = entry.target;
          const video = card.querySelector('video');
          if (!video) return;

          if (entry.isIntersecting && entry.intersectionRatio > 0.55) {
            if (card.dataset.synAutoplay === 'false') return;
            const attempt = video.play();
            if (attempt && typeof attempt.catch === 'function') {
              // Safari iOS bloque parfois la lecture : on reste sur l'affiche.
              attempt.catch(() => {});
            }
            card.classList.add('is-playing');
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: [0, 0.55, 0.9] }
    );

    cards.forEach((card) => {
      card.dataset.synVideoReady = 'true';
      loadObserver.observe(card);
      if (!prefersReducedMotion.matches) playObserver.observe(card);

      const video = card.querySelector('video');
      const toggle = card.querySelector('[data-syn-video-sound]');
      if (!video || !toggle) return;

      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        video.muted = !video.muted;
        card.classList.toggle('is-unmuted', !video.muted);
        toggle.setAttribute(
          'aria-label',
          video.muted ? 'Activer le son' : 'Couper le son'
        );
        if (video.paused) {
          const attempt = video.play();
          if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
        }
      });
    });
  }

  /* ======================================================================
     4. STICKY ADD TO CART
     ====================================================================== */

  function initStickyAtc() {
    const bar = document.querySelector('[data-syn-sticky-atc]');
    if (!bar) return;

    // Point de repère : le bloc d'achat principal de la fiche produit.
    const anchorSelector = bar.dataset.synAnchor || '[data-syn-atc-anchor]';
    const anchor = document.querySelector(anchorSelector);
    if (!anchor || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Visible seulement quand le bouton principal est passé au-dessus
          // de l'écran — jamais pendant que la cliente le voit déjà.
          const scrolledPast =
            !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.classList.toggle('is-visible', scrolledPast);
          bar.setAttribute('aria-hidden', scrolledPast ? 'false' : 'true');
        });
      },
      { threshold: 0 }
    );

    observer.observe(anchor);

    // Le CTA de la barre déclenche le vrai bouton d'ajout du formulaire.
    const cta = bar.querySelector('[data-syn-sticky-cta]');
    if (cta) {
      cta.addEventListener('click', (event) => {
        const target = anchor.querySelector(
          'button[type="submit"], [data-syn-atc-button]'
        );
        if (target && !target.disabled) {
          event.preventDefault();
          target.click();
        }
      });
    }
  }

  /* ======================================================================
     5. MODALS
     ====================================================================== */

  function openModal(dialog) {
    if (!dialog || dialog.open) return;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    document.documentElement.setAttribute('scroll-lock', '');
  }

  function closeModal(dialog) {
    if (!dialog || !dialog.open) return;
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  }

  function initModals() {
    // Ouverture
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-syn-modal-open]');
      if (!trigger) return;
      const id = trigger.getAttribute('data-syn-modal-open');
      const dialog = document.getElementById(id);
      if (!dialog) return;
      event.preventDefault();
      openModal(dialog);
    });

    // Fermeture
    document.addEventListener('click', (event) => {
      const closer = event.target.closest('[data-syn-modal-close]');
      if (closer) {
        const dialog = closer.closest('dialog');
        closeModal(dialog);
        return;
      }

      // Clic sur le fond : le backdrop appartient au <dialog> lui-même,
      // donc on vérifie que le clic est hors de la boîte de contenu.
      const dialog = event.target.closest('dialog.syn-modal');
      if (dialog && event.target === dialog) {
        const box = dialog.getBoundingClientRect();
        const inside =
          event.clientX >= box.left &&
          event.clientX <= box.right &&
          event.clientY >= box.top &&
          event.clientY <= box.bottom;
        if (!inside) closeModal(dialog);
      }
    });

    document.querySelectorAll('dialog.syn-modal').forEach((dialog) => {
      dialog.addEventListener('close', () => {
        if (!document.querySelector('dialog.syn-modal[open]')) {
          document.documentElement.removeAttribute('scroll-lock');
        }
      });
    });
  }

  /* ======================================================================
     6. POP-UP NEWSLETTER DE BIENVENUE
     ====================================================================== */

  var POPUP_KEY = 'synova:welcome-popup';

  function popupAlreadyHandled() {
    try {
      const raw = window.localStorage.getItem(POPUP_KEY);
      if (!raw) return false;
      const until = parseInt(raw, 10);
      if (Number.isNaN(until)) return true;
      return Date.now() < until;
    } catch (error) {
      // Navigation privée : on n'insiste pas.
      return true;
    }
  }

  function rememberPopup(days) {
    try {
      window.localStorage.setItem(
        POPUP_KEY,
        String(Date.now() + days * 24 * 60 * 60 * 1000)
      );
    } catch (error) {
      /* stockage indisponible — sans conséquence */
    }
  }

  function initWelcomePopup() {
    const dialog = document.querySelector('[data-syn-welcome-popup]');
    if (!dialog) return;

    const delay = parseInt(dialog.dataset.synDelay || '6', 10) * 1000;
    const frequency = parseInt(dialog.dataset.synFrequency || '30', 10);

    // Dans l'éditeur de thème, on affiche immédiatement pour la prévisualisation.
    if (document.documentElement.classList.contains('shopify-design-mode')) {
      window.setTimeout(() => openModal(dialog), 400);
      return;
    }

    if (popupAlreadyHandled()) return;

    window.setTimeout(() => {
      // Ne jamais recouvrir une autre modale déjà ouverte.
      if (document.querySelector('dialog.syn-modal[open]')) return;
      openModal(dialog);
      rememberPopup(frequency);
    }, delay);

    dialog.addEventListener('close', () => rememberPopup(frequency));
  }

  /* ======================================================================
     7. FEEDBACK D'AJOUT AU PANIER
     ====================================================================== */

  function showToast(message) {
    let toast = document.querySelector('[data-syn-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.setAttribute('data-syn-toast', '');
      toast.className = 'syn-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML =
        '<span class="syn-toast__icon" aria-hidden="true">' +
        '<svg viewBox="0 0 12 12" fill="none"><path d="M2 6.2 4.6 8.8 10 3.4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</span><span data-syn-toast-text></span>';
      document.body.appendChild(toast);
    }

    const text = toast.querySelector('[data-syn-toast-text]');
    if (text) text.textContent = message;

    toast.classList.add('is-visible');
    window.clearTimeout(toast._synTimer);
    toast._synTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 3200);
  }

  function pulseCartIcon() {
    // Élément personnalisé <cart-icon> d'Horizon (voir assets/cart-icon.js).
    const icon = document.querySelector('cart-icon, .header-actions__cart-icon');
    if (!icon) return;
    icon.classList.add('syn-cart-pulse', 'is-pulsing');
    window.setTimeout(() => icon.classList.remove('is-pulsing'), 750);
  }

  function isCartDrawerOpen() {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return false;
    const dialog = drawer.closest('dialog');
    return Boolean(dialog && dialog.open);
  }

  function initCartFeedback() {
    /**
     * Shopify Standard Events : `cart:lines-update` est émis après chaque
     * modification du panier. La propriété `action` vaut 'add' lors d'un ajout.
     * (Nom littéral confirmé dans cdn.shopify.com/storefront/standard-events.js.)
     */
    document.addEventListener('cart:lines-update', (event) => {
      const action = event.action || (event.detail && event.detail.action);
      if (action !== 'add') return;

      pulseCartIcon();

      /*
       * Horizon ouvre déjà le tiroir panier automatiquement à l'ajout : le
       * toast ferait doublon. On ne l'affiche que si le tiroir est resté fermé
       * (panier en page, ou ouverture automatique désactivée).
       */
      window.setTimeout(() => {
        if (!isCartDrawerOpen()) showToast('Ajouté au panier ♡');
      }, 400);
    });
  }

  /* ======================================================================
     8. BARRE D'ANNONCES ROTATIVE
     ====================================================================== */

  function initAnnouncements() {
    document.querySelectorAll('[data-syn-announcement]').forEach((root) => {
      if (root.dataset.synAnnouncementReady === 'true') return;
      root.dataset.synAnnouncementReady = 'true';

      const items = root.querySelectorAll('.syn-announcement__item');
      if (items.length < 2) return;

      const interval = parseInt(root.dataset.synInterval || '5', 10) * 1000;
      let index = 0;
      let timer = null;

      function advance() {
        items[index].classList.remove('is-active');
        index = (index + 1) % items.length;
        items[index].classList.add('is-active');
      }

      function start() {
        stop();
        timer = window.setInterval(advance, interval);
      }

      function stop() {
        if (timer) window.clearInterval(timer);
        timer = null;
      }

      // On suspend la rotation hors écran et au survol : pas de travail inutile.
      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
      document.addEventListener('visibilitychange', () => {
        document.hidden ? stop() : start();
      });

      start();
    });
  }

  /* ======================================================================
     9. HEADER — état « défilé »
     ====================================================================== */

  function initHeaderScroll() {
    const root = document.documentElement;

    /*
     * Sentinelle d'un pixel placée en haut du contenu : tant qu'elle est
     * visible, on est au sommet. Bien plus économique qu'un écouteur de
     * défilement, et cela fonctionne aussi bien quand c'est .page-wrapper
     * qui défile (cas du desktop dans Horizon) que la fenêtre.
     */
    const scroller = getScroller();
    const host =
      scroller === window ? document.body : /** @type {HTMLElement} */ (scroller);
    if (!host || !('IntersectionObserver' in window)) return;

    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText =
      'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;';
    host.prepend(sentinel);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          root.classList.toggle('syn-header-scrolled', !entry.isIntersecting);
        });
      },
      { root: scroller === window ? null : scroller, threshold: 0 }
    );

    observer.observe(sentinel);
  }

  /* ======================================================================
     INITIALISATION
     ====================================================================== */

  function init() {
    initReveal();
    initScrollers();
    initVideos();
    initStickyAtc();
    initModals();
    initWelcomePopup();
    initCartFeedback();
    initAnnouncements();
    initHeaderScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Réinitialisation dans l'éditeur de thème après rechargement d'une section.
  document.addEventListener('shopify:section:load', () => {
    initReveal();
    initScrollers();
    initVideos();
    initStickyAtc();
    initAnnouncements();
  });

  document.addEventListener('shopify:section:select', initReveal);
})();
